import * as THREE from 'three';
import {mergeGeometries} from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import {markCadObjectAsRuntimeDerived} from '../../document';
import {ViewOrientationMaterialFactory} from '../../material/ViewOrientationMaterialFactory';
import {getStandardViewDefinition} from './standardViewDefinitions';
import {StandardViewType} from './standardViewTypes';
import {ViewOrientationCornerMesh} from './ViewOrientationCornerMesh';
import {ViewOrientationEdgeMesh} from './ViewOrientationEdgeMesh';
import {ViewOrientationElementMesh} from './ViewOrientationElementMesh';
import {ViewOrientationFaceMesh} from './ViewOrientationFaceMesh';
import {ViewOrientationFaceProxyMesh} from './ViewOrientationFaceProxyMesh';


/**
 * 视图定向元素相对当前相机的朝向枚举。
 */
enum ViewOrientationFacingSide {
  /** 朝向当前相机。 */
  Front = 'front',
  /** 背向当前相机。 */
  Back = 'back',
}

/**
 * 视图定向独立网格集合。
 *
 * 设计说明：
 * 1. 该类继承 Group，当前阶段负责组合 6 个大面、12 个连接面与 8 个角点面；
 * 2. 三类几何的生成职责均下沉到各自 Mesh 子类，容器层只保留组合和交互状态；
 * 3. 容器只负责统一挂载、索引和交互态，不承载具体几何计算；
 * 4. 整棵子树属于运行时视图辅助对象，由相机管理器重建，不参与模型持久化。
 */
export class ViewOrientationMesh extends THREE.Group {
  /** 朝向判定阈值。 */
  private static readonly FACING_EPSILON = 1e-6;


    /**
   * 视图定向网格所在 Three.js layer（用于预览相机过滤）。
   * 预览相机会排除此 layer，从而不渲染视角定位 mesh 自身。
   */
  public static readonly MESH_LAYER = 31;

  /** 视图定向网格默认边长（供 CameraManager 做包围盒缩放使用，来源于基类统一定义）。 */
  public static readonly DEFAULT_SIZE = ViewOrientationElementMesh.SIZE;

  /** 视图定向网格默认渲染顺序（来源于基类统一定义，此处供容器层 +1/+2 偏移使用）。 */
  private static readonly RENDER_ORDER = ViewOrientationElementMesh.RENDER_ORDER;

  /** 视图定向网格默认透明度。 */
  private static readonly OPACITY = 0.6;

  /** 视图定向器朝前元素透明度。 */
  private static readonly FRONT_OPACITY = 0;

  /** 视图定向器背向大面透明度。 */
  private static readonly BACK_FACE_OPACITY = 0;

  /** 视图定向器背向边面透明度。 */
  private static readonly BACK_EDGE_OPACITY = 0.5;

  /** 视图定向器背向角点面透明度。 */
  private static readonly BACK_CORNER_OPACITY = 0.5;

  /** 视图定向网格 hover 高亮颜色。 */
  private static readonly HOVER_COLOR = '#6464ff';

  /** 背面大面代理面最大偏移距离。 */
  private static readonly FACE_PROXY_MAX_OFFSET = 4.5;

  /** 背面大面代理面开始回收偏移的朝向阈值。 */
  private static readonly FACE_PROXY_TRANSITION_START_DOT = 0;

  /** 背面大面代理面完全隐藏的朝向阈值。 */
  private static readonly FACE_PROXY_HIDE_DOT = 0.24;


    /** 主面网格列表。 */
  private readonly faceMeshes: ViewOrientationFaceMesh[];

  /** 背面大面代理网格列表。 */
  private readonly faceProxyMeshes: ViewOrientationFaceProxyMesh[];

  /** 连接面网格列表。 */
  private readonly edgeMeshes: ViewOrientationEdgeMesh[];

  /** 角点面网格列表。 */
  private readonly cornerMeshes: ViewOrientationCornerMesh[];

  /** 元素网格索引表。 */
  private readonly meshMap = new Map<StandardViewType, ViewOrientationElementMesh>();

  /** 元素默认颜色索引表。 */
  private readonly baseColorMap = new Map<StandardViewType, THREE.Color>();

  /** 背面大面代理索引表。 */
  private readonly faceProxyMeshMap = new Map<StandardViewType, ViewOrientationFaceProxyMesh>();

  /** 当前 hover 视图。 */
  private hoveredViewType: StandardViewType | null = null;

  /** 当前 hover 的具体 mesh。 */
  private hoveredMesh: ViewOrientationElementMesh | null = null;

  /** 当前 hover 联动高亮网格集合。 */
  private readonly hoveredHighlightMeshSet = new Set<ViewOrientationElementMesh>();

  /** 当前激活视图。 */
  private activeViewType: StandardViewType | null = null;

  /**
   * 共享边线网格，在所有子 mesh 构建完成后由 buildSharedOutlineLine 创建。
   * 所有独立网格的轮廓线合并为单一 LineSegments，避免相邻面边界重叠线段。
   */
  private sharedOutlineLine: THREE.LineSegments<THREE.EdgesGeometry, THREE.LineBasicMaterial> | null = null;

  /** 当前 hover 联动高亮组的专属高亮边线覆盖层。 */
  private hoveredOutlineLine: THREE.LineSegments<THREE.EdgesGeometry, THREE.LineBasicMaterial> | null = null;

  /** 最近一次同步透明度时使用的相机位置。 */
  private readonly lastCameraPosition: THREE.Vector3 = new THREE.Vector3();

  /** 最近一次同步透明度时使用的观察目标。 */
  private readonly lastCameraTarget: THREE.Vector3 = new THREE.Vector3();

  /** 是否已缓存相机状态。 */
  private hasCameraSnapshot: boolean = false;

  /**
   * 构造视图定向网格集合。
   */
  public constructor() {
    super();
    // 在对象创建时确定持久化边界，防止打开或悬停定向器后辅助网格被误判为缺少 BRep 的模型。
    markCadObjectAsRuntimeDerived(this);
    this.name = 'view-orientation-mesh';
    this.faceMeshes = [
      new ViewOrientationFaceMesh(StandardViewType.Right),
      new ViewOrientationFaceMesh(StandardViewType.Top),
      new ViewOrientationFaceMesh(StandardViewType.Front),
      new ViewOrientationFaceMesh(StandardViewType.Left),
      new ViewOrientationFaceMesh(StandardViewType.Bottom),
      new ViewOrientationFaceMesh(StandardViewType.Back),
    ];
    this.faceProxyMeshes = this.faceMeshes.map((mesh) => new ViewOrientationFaceProxyMesh(mesh.viewType, mesh));
    this.edgeMeshes = [
      new ViewOrientationEdgeMesh(StandardViewType.FrontTop),
      new ViewOrientationEdgeMesh(StandardViewType.FrontBottom),
      new ViewOrientationEdgeMesh(StandardViewType.FrontRight),
      new ViewOrientationEdgeMesh(StandardViewType.FrontLeft),
      new ViewOrientationEdgeMesh(StandardViewType.BackTop),
      new ViewOrientationEdgeMesh(StandardViewType.BackBottom),
      new ViewOrientationEdgeMesh(StandardViewType.BackRight),
      new ViewOrientationEdgeMesh(StandardViewType.BackLeft),
      new ViewOrientationEdgeMesh(StandardViewType.TopRight),
      new ViewOrientationEdgeMesh(StandardViewType.BottomRight),
      new ViewOrientationEdgeMesh(StandardViewType.TopLeft),
      new ViewOrientationEdgeMesh(StandardViewType.BottomLeft),
    ];
    this.cornerMeshes = [
      new ViewOrientationCornerMesh(StandardViewType.FrontTopRight),
      new ViewOrientationCornerMesh(StandardViewType.FrontTopLeft),
      new ViewOrientationCornerMesh(StandardViewType.FrontBottomRight),
      new ViewOrientationCornerMesh(StandardViewType.FrontBottomLeft),
      new ViewOrientationCornerMesh(StandardViewType.BackTopRight),
      new ViewOrientationCornerMesh(StandardViewType.BackTopLeft),
      new ViewOrientationCornerMesh(StandardViewType.BackBottomRight),
      new ViewOrientationCornerMesh(StandardViewType.BackBottomLeft),
    ];
    this.buildMeshes();
  }

  /**
   * 读取指定标准视图对应的原始网格。
   *
   * @param viewType 标准视图类型。
   * @returns 对应原始网格；未命中时返回 null。
   */
  public getMeshByViewType(
    viewType: StandardViewType,
  ): ViewOrientationElementMesh | null {
    return this.meshMap.get(viewType) ?? null;
  }

  /**
   * 读取全部可拾取网格。
   *
   * 设计说明：
   * 1. 原始 26 个元素始终参与拾取；
   * 2. 背面大面代理面仅在可见时加入拾取链；
   * 3. 返回快照数组，避免外部直接改写内部集合。
   *
   * @returns 全部可拾取网格快照。
   */
  public getAllMeshes(): readonly ViewOrientationElementMesh[] {
    const meshes: ViewOrientationElementMesh[] = Array.from(this.meshMap.values());
    this.faceProxyMeshes.forEach((mesh) => {
      if (mesh.visible) {
        meshes.push(mesh);
      }
    });
    return meshes;
  }

  /**
   * 根据当前相机方向同步各元素透明度。
   *
   * 规则：
   * 1. 朝前的大面、边面、角点面透明度全部为 0；
   * 2. 背向的大面透明度为 0；
   * 3. 背向的边面与角点面透明度为 0.5；
   * 4. 背面大面代理面会沿法线方向逐步回收到原始大面位置；
   * 5. 当前原始大面已足够暴露时，对应代理面会直接隐藏；
   * 6. 透明度与 hover/active 颜色解耦，只改 opacity，不改 color。
   *
   * @param cameraPosition 当前相机位置。
   * @param target 当前观察目标点。
   */
  public syncOpacityByCamera(
    cameraPosition: THREE.Vector3,
    target: THREE.Vector3,
  ): void {
    this.lastCameraPosition.copy(cameraPosition);
    this.lastCameraTarget.copy(target);
    this.hasCameraSnapshot = true;

    const viewDirection = cameraPosition.clone().sub(target);
    if (viewDirection.lengthSq() <= Number.EPSILON) {
      return;
    }

    viewDirection.normalize();

    this.meshMap.forEach((mesh) => {
      const definition = getStandardViewDefinition(mesh.viewType);
      const elementDirection = new THREE.Vector3(...definition.direction).normalize();
      const facingSide = elementDirection.dot(viewDirection) >= -ViewOrientationMesh.FACING_EPSILON
        ? ViewOrientationFacingSide.Front
        : ViewOrientationFacingSide.Back;
      const nextOpacity = this.resolveMeshOpacity(mesh, facingSide);

      if (this.hoveredHighlightMeshSet.has(mesh)) {
        this.applyMaterialOpacity(mesh.material, ViewOrientationMesh.OPACITY);
        return;
      }

      this.applyMaterialOpacity(mesh.material, nextOpacity);
    });

    this.syncFaceProxyMeshes(viewDirection);
  }

  /**
   * 设置当前 hover 视图。
   *
   * @param viewType 当前 hover 的标准视图类型。
   */
  public setHoveredViewType(viewType: StandardViewType | null): void {
    this.setHoveredMesh(viewType === null ? null : this.meshMap.get(viewType) ?? null);
  }

  /**
   * 设置当前 hover 的具体 mesh。
   *
   * @param mesh 当前 hover 命中的 mesh。
   */
  public setHoveredMesh(mesh: ViewOrientationElementMesh | null): void {
    if (this.hoveredMesh === mesh) {
      return;
    }

    this.hoveredMesh = mesh;
    this.hoveredViewType = mesh?.viewType ?? null;
    this.syncHoveredHighlightMeshSet(mesh);

    if (this.hasCameraSnapshot) {
      this.syncOpacityByCamera(this.lastCameraPosition, this.lastCameraTarget);
    }

    this.updateMeshVisualStates();
    this.refreshHoveredOutlineLine();
  }

  /**
   * 设置当前激活视图。
   *
   * @param viewType 当前激活的标准视图类型。
   */
  public setActiveViewType(viewType: StandardViewType | null): void {
    if (this.activeViewType === viewType) {
      return;
    }

    this.activeViewType = viewType;
    this.updateMeshVisualStates();
  }

  /**
   * 清空交互状态。
   */
  public clearInteractionState(): void {
    this.hoveredMesh = null;
    this.hoveredViewType = null;
    this.hoveredHighlightMeshSet.clear();
    this.activeViewType = null;
    if (this.hasCameraSnapshot) {
      this.syncOpacityByCamera(this.lastCameraPosition, this.lastCameraTarget);
    }
    this.updateMeshVisualStates();
    this.clearHoveredOutlineLine();
  }

  /**
   * 释放全部几何与材质资源。
   */
  public dispose(): void {
    this.faceMeshes.forEach((mesh) => {
      mesh.dispose();
    });
    this.faceProxyMeshes.forEach((mesh) => {
      mesh.dispose();
    });
    this.edgeMeshes.forEach((mesh) => {
      mesh.dispose();
    });
    this.cornerMeshes.forEach((mesh) => {
      mesh.dispose();
    });
    this.meshMap.forEach((mesh) => {
      if (mesh.parent === this) {
        this.remove(mesh);
      }
    });
    this.faceProxyMeshes.forEach((mesh) => {
      if (mesh.parent === this) {
        this.remove(mesh);
      }
    });
    this.meshMap.clear();
    this.baseColorMap.clear();
    this.faceProxyMeshMap.clear();
    this.hoveredMesh = null;
    this.hoveredViewType = null;
    this.hoveredHighlightMeshSet.clear();
    this.activeViewType = null;
    if (this.sharedOutlineLine) {
      this.sharedOutlineLine.geometry.dispose();
      this.sharedOutlineLine.material.dispose();
      this.remove(this.sharedOutlineLine);
      this.sharedOutlineLine = null;
    }
    this.clearHoveredOutlineLine();
  }

  /**
   * 构建当前独立交互 mesh。
   */
  private buildMeshes(): void {
    this.faceMeshes.forEach((mesh) => {
      this.add(mesh);
      mesh.layers.set(ViewOrientationMesh.MESH_LAYER);
      this.baseColorMap.set(viewTypeOrThrow(mesh), mesh.material.color.clone());
      this.meshMap.set(viewTypeOrThrow(mesh), mesh);
    });
    this.faceProxyMeshes.forEach((mesh) => {
      this.add(mesh);
      mesh.layers.set(ViewOrientationMesh.MESH_LAYER);
      this.faceProxyMeshMap.set(mesh.viewType, mesh);
    });
    this.edgeMeshes.forEach((mesh) => {
      this.add(mesh);
      mesh.layers.set(ViewOrientationMesh.MESH_LAYER);
      this.baseColorMap.set(viewTypeOrThrow(mesh), mesh.material.color.clone());
      this.meshMap.set(viewTypeOrThrow(mesh), mesh);
    });
    this.cornerMeshes.forEach((mesh) => {
      this.add(mesh);
      mesh.layers.set(ViewOrientationMesh.MESH_LAYER);
      this.baseColorMap.set(viewTypeOrThrow(mesh), mesh.material.color.clone());
      this.meshMap.set(viewTypeOrThrow(mesh), mesh);
    });

    this.buildSharedOutlineLine();
    this.updateMeshVisualStates();
  }

  /**
   * 构建共享边线网格。
   *
   * 将全部原始子 mesh 几何体应用各自局部变换后合并，
   * 以 EdgesGeometry 生成单一 LineSegments，避免相邻面边界重叠双线。
   */
  private buildSharedOutlineLine(): void {
    const clonedGeos: THREE.BufferGeometry[] = [];
    this.meshMap.forEach((mesh) => {
      mesh.updateMatrix();
      const cloned = mesh.geometry.clone().applyMatrix4(mesh.matrix);
      clonedGeos.push(cloned);
    });

    const merged = mergeGeometries(clonedGeos, false);
    clonedGeos.forEach((geometry) => geometry.dispose());

    if (!merged) {
      return;
    }

    const edgesGeo = new THREE.EdgesGeometry(merged);
    merged.dispose();

    const mat = ViewOrientationMaterialFactory.createOutlineMaterial();
    this.sharedOutlineLine = new THREE.LineSegments(edgesGeo, mat);
    this.sharedOutlineLine.renderOrder = ViewOrientationMesh.RENDER_ORDER + 1;
    this.sharedOutlineLine.layers.set(ViewOrientationMesh.MESH_LAYER);
    this.add(this.sharedOutlineLine);
  }

  /**
   * 刷新当前 hover 联动高亮组的专属高亮边线覆盖层。
   *
   * 共享边线用于静态展示；hover 反馈通过独立覆盖层仅高亮当前关联组边线，
   * 避免把所有共享边线一起染色。
   */
  private refreshHoveredOutlineLine(): void {
    this.clearHoveredOutlineLine();

    if (this.hoveredHighlightMeshSet.size === 0) {
      return;
    }

    const clonedGeometries: THREE.BufferGeometry[] = [];
    this.hoveredHighlightMeshSet.forEach((mesh) => {
      mesh.updateMatrix();
      clonedGeometries.push(mesh.geometry.clone().applyMatrix4(mesh.matrix));
    });

    const mergedGeometry = mergeGeometries(clonedGeometries, false);
    clonedGeometries.forEach((geometry) => geometry.dispose());
    if (!mergedGeometry) {
      return;
    }

    const hoveredEdges = new THREE.EdgesGeometry(mergedGeometry);
    mergedGeometry.dispose();

    const hoveredMaterial = ViewOrientationMaterialFactory.createOutlineMaterial();
    hoveredMaterial.color.set(ViewOrientationMesh.HOVER_COLOR);

    this.hoveredOutlineLine = new THREE.LineSegments(hoveredEdges, hoveredMaterial);
    this.hoveredOutlineLine.renderOrder = ViewOrientationMesh.RENDER_ORDER + 2;
    this.hoveredOutlineLine.layers.set(ViewOrientationMesh.MESH_LAYER);
    this.add(this.hoveredOutlineLine);
  }

  /** 释放并移除 hover 专属边线覆盖层。 */
  private clearHoveredOutlineLine(): void {
    if (!this.hoveredOutlineLine) {
      return;
    }

    this.hoveredOutlineLine.geometry.dispose();
    this.hoveredOutlineLine.material.dispose();
    this.remove(this.hoveredOutlineLine);
    this.hoveredOutlineLine = null;
  }

  /**
   * 全量刷新所有网格的交互视觉状态。
   */
  private updateMeshVisualStates(): void {
    this.meshMap.forEach((mesh, viewType) => {
      const baseColor = this.baseColorMap.get(viewType);
      if (!baseColor) {
        return;
      }

      const nextColor = baseColor.clone();
      const isHovered = this.hoveredHighlightMeshSet.has(mesh);
      const isActive = viewType === this.activeViewType;

      if (isHovered) {
        mesh.material.color.set(ViewOrientationMesh.HOVER_COLOR);
      } else if (isActive) {
        nextColor.lerp(new THREE.Color('#ffffff'), 0.18);
        mesh.material.color.copy(nextColor);
      } else {
        mesh.material.color.copy(nextColor);
      }
    });

    this.faceProxyMeshes.forEach((mesh) => {
      const baseColor = this.baseColorMap.get(mesh.viewType);
      if (!baseColor) {
        return;
      }

      if (this.hoveredHighlightMeshSet.has(mesh)) {
        mesh.material.color.set(ViewOrientationMesh.HOVER_COLOR);
      } else {
        mesh.material.color.copy(baseColor);
      }
    });
  }

  /**
   * 同步背面大面代理面的偏移、显隐与透明度。
   *
   * 当对应大面逐渐转向前方时，代理面会沿法线方向逐步回收到原始大面位置；
   * 当已足够看到原始大面时，代理面直接隐藏并退出拾取链。
   *
   * @param viewDirection 当前相机观察方向。
   */
  private syncFaceProxyMeshes(viewDirection: THREE.Vector3): void {
    this.faceProxyMeshes.forEach((mesh) => {
      const definition = getStandardViewDefinition(mesh.viewType);
      const faceDirection = new THREE.Vector3(...definition.direction).normalize();
      const dot = faceDirection.dot(viewDirection);
      const visibilityFactor = this.resolveFaceProxyVisibilityFactor(dot);
      const nextOffset = ViewOrientationMesh.FACE_PROXY_MAX_OFFSET * visibilityFactor;

      mesh.setOffsetDistance(nextOffset);
      mesh.visible = visibilityFactor > ViewOrientationMesh.FACING_EPSILON;

      // 代理面默认使用背面角面透明度（0.5），hover 时使用高亮透明度
      const nextOpacity = this.hoveredHighlightMeshSet.has(mesh)
        ? ViewOrientationMesh.OPACITY
        : ViewOrientationMesh.BACK_CORNER_OPACITY;
      this.applyMaterialOpacity(mesh.material, nextOpacity);
    });
  }

  /**
   * 将法线朝向点积映射为代理面可见系数。
   *
   * @param dot 面法线与当前观察方向点积。
   * @returns 0 到 1 的可见系数。
   */
  private resolveFaceProxyVisibilityFactor(dot: number): number {
    if (dot <= ViewOrientationMesh.FACE_PROXY_TRANSITION_START_DOT) {
      return 1;
    }

    if (dot >= ViewOrientationMesh.FACE_PROXY_HIDE_DOT) {
      return 0;
    }

    const range = ViewOrientationMesh.FACE_PROXY_HIDE_DOT - ViewOrientationMesh.FACE_PROXY_TRANSITION_START_DOT;
    return 1 - (dot - ViewOrientationMesh.FACE_PROXY_TRANSITION_START_DOT) / range;
  }

  /**
   * 统一应用材质透明度相关参数。
   *
   * @param material 目标材质。
   * @param opacity 目标透明度。
   */
  private applyMaterialOpacity(material: THREE.MeshBasicMaterial, opacity: number): void {
    material.opacity = opacity;
    material.transparent = opacity < 1;
    material.depthWrite = opacity >= 1;
  }

  /**
   * 按元素类型和朝向解析透明度。
   *
   * @param mesh 目标元素网格。
   * @param facingSide 相对当前相机的朝向。
   * @returns 目标透明度。
   */
  private resolveMeshOpacity(
    mesh: ViewOrientationElementMesh,
    facingSide: ViewOrientationFacingSide,
  ): number {
    if (facingSide === ViewOrientationFacingSide.Front) {
      return ViewOrientationMesh.FRONT_OPACITY;
    }

    if (mesh instanceof ViewOrientationEdgeMesh) {
      return ViewOrientationMesh.BACK_EDGE_OPACITY;
    }

    if (mesh instanceof ViewOrientationCornerMesh) {
      return ViewOrientationMesh.BACK_CORNER_OPACITY;
    }

    return ViewOrientationMesh.BACK_FACE_OPACITY;
  }

  /**
   * 同步当前 hover 对应的联动高亮网格集合。
   *
   * @param mesh 当前 hover 命中的 mesh。
   */
  private syncHoveredHighlightMeshSet(mesh: ViewOrientationElementMesh | null): void {
    this.hoveredHighlightMeshSet.clear();

    this.resolveHoveredHighlightMeshes(mesh).forEach((highlightMesh) => {
      this.hoveredHighlightMeshSet.add(highlightMesh);
    });
  }

  /**
   * 解析当前 hover 需要高亮的网格集合。
   *
   * 规则：任意类型 hover 仅高亮当前命中 mesh 本身及对应边线，不联动相邻主面或边面。
   *
   * @param mesh 当前 hover 命中的 mesh。
   * @returns 需要高亮的网格集合。
   */
  private resolveHoveredHighlightMeshes(mesh: ViewOrientationElementMesh | null): readonly ViewOrientationElementMesh[] {
    if (!mesh) {
      return [];
    }

    return [mesh];
  }

}

/**
 * 读取 mesh 自带的标准视图标识。
 *
 * @param mesh 目标 mesh。
 * @returns 标准视图标识。
 */
function viewTypeOrThrow(mesh: ViewOrientationElementMesh): StandardViewType {
  return mesh.viewType;
}
